import React from "react";
import * as T from "@types";
import { App } from "./components/App";
import { SocketProvider, AppProvider } from "./providers";
import { createRoot, RootType } from "react-dom/client";

declare global {
  interface Window {
    SRTInstance: {
      socket: WebSocket;
      root: RootType;
    } | null;
    SRTConfig: {
      $container: string;
      accessToken: string;
      darkMode: boolean;
      onTranscript: (transcript: T.TranscriptionWSResponse) => void;
      onReady?: (config: T.AppConfig) => void;
    };
  }
}

export type EmbedSRTParams = {
  $container: string;
  accessToken?: string;
  darkMode?: boolean;
  onTranscript?: (transcript: T.TranscriptionWSResponse) => void;
  onReady?: (config: T.AppConfig) => void;
};

window.SRTInstance = null;

const embedSRT = ({ $container, accessToken, darkMode, onTranscript, onReady }: EmbedSRTParams) => {
  const $containerElement = document.getElementById($container);
  const appAccessToken = accessToken || window.SRTConfig.accessToken;
  const appDarkMode = darkMode || (window.SRTConfig && window.SRTConfig.darkMode) || false;
  const appOnTranscript = onTranscript || (window.SRTConfig && window.SRTConfig.onTranscript) || undefined;
  const appOnReady = onReady || (window.SRTConfig && window.SRTConfig.onReady) || undefined;

  if (!appAccessToken) {
    console.error("SRT-Embed:  AccessToken not provided.");
    return;
  }

  if (!$containerElement) {
    console.error(`SRT-Embed: #${$container} not found.`);
    return;
  }

  if ($containerElement) {
    const root = createRoot($containerElement);

    root.render(
      <AppProvider accessToken={appAccessToken} initialDarkMode={appDarkMode}>
        <SocketProvider
          onTranscript={appOnTranscript}
          ref={(socketRef) => {
            window.SRTInstance = { socket: socketRef, root };
          }}
        >
          <App onReady={appOnReady} />
        </SocketProvider>
      </AppProvider>
    );
  }
};

const destroySRT = () => {
  if (window.SRTInstance?.socket) {
    window.SRTInstance.socket.close();
    setTimeout(() => {
      window.SRTInstance.root.unmount();
    }, 150);
  }
};

if (window.SRTConfig && window.SRTConfig.accessToken && window.SRTConfig.$container) {
  document.addEventListener("DOMContentLoaded", function () {
    embedSRT({ $container: window.SRTConfig.$container });
  });
}

export { embedSRT, destroySRT };
export { App } from "./components/App";
export { useSocket, SocketProvider } from "./providers";
